import { TranslationKey } from './../../../../../../types/translation/TranslationKey';
import React, { useContext, useState } from 'react';
import { t } from '../../../../../../types/translation/Translator';
import { CreateTriggerInput, Trigger, TriggerEntityType } from '../../../../../../types/trigger';
import Selector from '../../../../Common/Selector';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import { LotContext } from '../../../../../../context/LotContext';
import { testIds } from '../../../../../../util/identifiers/identifiers.util';
import ModalPane from '../../../../../../VentoryUI/components/common/Modal/components/ModalPane';
import { BackButtonTemplate } from '../../../../../../VentoryUI/components/common/Button/Templates/BackButton';
import { NextButtonTemplate } from '../../../../../../VentoryUI/components/common/Button/Templates/NextButton/NextButton';
import ToggleButton from '../../../../Common/ToggleButton';
import { TagContext } from '../../../../../../context/TagContext';
import { EntityType } from '../../../../../../types/comment';
import { toMap } from '../../../../../../util/map.util';

interface CreateTriggerEntityIdsPaneInputProps {
  trigger: CreateTriggerInput;
  setTrigger: (trigger: Trigger) => void;
  next: () => void;
  back: () => void;
}

export function CreateTriggerEntityIdsPane({ trigger, setTrigger, next, back }: CreateTriggerEntityIdsPaneInputProps) {
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { lots } = useContext(LotContext);

  return (
    <ModalPane
      footerButtons={[BackButtonTemplate(back), NextButtonTemplate(next)]}
      testId={testIds.createTriggerEntityIdsPane}
    >
      {trigger.entityType === TriggerEntityType.pmd ? (
        <ProductMasterDataEntityTriggerSelect trigger={trigger} setTrigger={setTrigger} />
      ) : (
        <Selector
          testId={testIds.triggerEntityIds}
          placeholder={t(TranslationKey.triggerEntityIds).singular.label}
          values={[...[...lots.values()].filter(lot => lot.companyId === trigger.companyId).map(lot => lot.id)]}
          checkedValues={trigger.entityIds}
          toText={item => lots.get(item)?.number || 'Unknown'}
          onChange={checked => setTrigger(trigger.withEntityIds(checked))}
        />
      )}
    </ModalPane>
  );
}

enum ProductMasterDataTriggerSelectType {
  pmd = 'pmd',
  tag = 'tag',
}

interface ProductMasterDataEntityTriggerSelectProps {
  trigger: Trigger;
  setTrigger: (trigger: Trigger) => void;
}

function ProductMasterDataEntityTriggerSelect({ trigger, setTrigger }: ProductMasterDataEntityTriggerSelectProps) {
  const { productMasterData } = useContext(ProductMasterDataContext);

  const { tags } = useContext(TagContext);
  const pmdTags = toMap(
    [...tags.values()].filter(t => t.type === EntityType.pmd),
    'id',
  );

  const [selectType, _setSelectType] = useState(ProductMasterDataTriggerSelectType.pmd);
  const setSelectType = (type: ProductMasterDataTriggerSelectType) => {
    if (type === selectType) return;
    _setSelectType(type);
    setTrigger(trigger.withEntityIds([]));
  };

  const Content = () => {
    switch (selectType) {
      case ProductMasterDataTriggerSelectType.pmd:
        return (
          <Selector
            values={[...productMasterData.values()].filter(p => p.companyId === trigger.companyId).map(p => p.id)}
            checkedValues={trigger.entityIds}
            onChange={checked => setTrigger(trigger.withEntityIds(checked))}
            placeholder={t(TranslationKey.selectProductReference).plural.upper}
            toText={id => productMasterData.get(id)?.productName || ''}
            toElement={id => {
              const pmd = productMasterData.get(id);
              if (!pmd) return <></>;
              return (
                <div className='grid gap-1'>
                  <p className='text-sm text-black'>{pmd.productName}</p>
                  <p className='text-sm text-subtitle'>{pmd.productNumber}</p>
                </div>
              );
            }}
          />
        );
      case ProductMasterDataTriggerSelectType.tag:
        return (
          <Selector
            values={[...pmdTags.values()].filter(t => t.companyId === trigger.companyId).map(t => `tag::${t.id}`)}
            checkedValues={trigger.entityIds}
            onChange={checked => setTrigger(trigger.withEntityIds(checked))}
            placeholder={t(TranslationKey.selectTag).plural.upper}
            toText={tag => tags.get(tag.split('tag::')[1])?.name || ''}
          />
        );
    }
  };

  return (
    <div className='grid gap-4'>
      {pmdTags.size ? (
        <ToggleButton
          selected={selectType}
          onChange={v => setSelectType(v as ProductMasterDataTriggerSelectType)}
          values={Object.values(ProductMasterDataTriggerSelectType)}
          text={v => {
            switch (v) {
              case ProductMasterDataTriggerSelectType.pmd:
                return t(TranslationKey.productReferenceData).singular.upper;
              case ProductMasterDataTriggerSelectType.tag:
                return t(TranslationKey.tag).plural.upper;
            }
            return '';
          }}
        />
      ) : null}
      {Content()}
    </div>
  );
}
